import { useState } from "react";
import { Modal } from "react-bootstrap";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";

import { z } from "zod";
import { Input } from "src/components/common/form/input";
import { Form } from "../common/form";
import { usePostCalendarMutation } from "src/services/react-query-apis/post-calendar";
import { toast } from "react-toastify";
import { usePutCalendarMutation } from "src/services/react-query-apis/put-calendar";
import { useCalendarByDateRangeQuery } from "src/services/react-query-apis/get-calendar-by-date-range";
import moment from "moment";
import { usePostCalendarGenerateMonthMutation } from "src/services/react-query-apis/post-calendar-generate-month";
import { Select } from "../common/form/select";

const CalendarLegend = () => {
  return (
    <>
      <div className="d-flex gap-3 align-items-center mt-3">
        <div className="d-flex align-items-center w-25">
          <div
            className="custom-calendar me-2 border border-dark"
            style={{ width: 20, height: 20 }}
          ></div>
          <span>通常授業</span>
        </div>
        <div className="d-flex align-items-center w-25">
          <div
            className="custom-calendar custom-calendar-test me-2 border border-dark"
            style={{ width: 20, height: 20 }}
          ></div>
          <span>進級テスト</span>
        </div>
        <div className="d-flex align-items-center w-25">
          <div
            className="custom-calendar custom-calendar-holiday me-2"
            style={{ width: 20, height: 20 }}
          ></div>
          <span>休校日</span>
        </div>
      </div>
      <div className="d-flex gap-3 align-items-center mt-3">
        <div className="d-flex align-items-center w-25">
          <div
            className="custom-calendar custom-calendar-closed me-2"
            style={{ width: 20, height: 20 }}
          ></div>
          <span>休業日</span>
        </div>
        <div className="d-flex align-items-center w-25">
          <div
            className="custom-calendar custom-calendar-selected me-2"
            style={{ width: 20, height: 20 }}
          ></div>
          <span>選択中</span>
        </div>
        <div className="d-flex align-items-center w-25">
          <div
            className="custom-calendar react-calendar__tile--now me-2"
            style={{ width: 20, height: 20 }}
          ></div>
          <span>今日</span>
        </div>
      </div>
    </>
  );
};

const courseCalendarSchema = z.object({
  date: z.string().min(1, "日付を入力してください"),
  lesson_type: z.enum(["test", "regular", "none"], {
    errorMap: () => ({ message: "授業種別を選択してください" }),
  }),
  school_status: z.enum(["business", "holiday", "closed"], {
    errorMap: () => ({ message: "学校種別を選択してください" }),
  }),
});

const DateForm = ({ calendar }) => {
  const mutationConfig = {
    onSuccess: data => {
      toast.success(data?.message);
    },
  };

  const postCourseCalendarMutation = usePostCalendarMutation({
    mutationConfig,
  });

  const putCourseCalendarMutation = usePutCalendarMutation({
    mutationConfig,
  });

  return (
    <Form
      onSubmit={values => {
        if (calendar?.id) {
          putCourseCalendarMutation.mutate({
            id: calendar?.id,
            ...values,
          });
        } else {
          postCourseCalendarMutation.mutate(values);
        }
      }}
      schema={courseCalendarSchema}
      options={{
        defaultValues: {
          date: calendar?.date || "",
          lesson_type: calendar?.lesson_type || "",
          school_status: calendar?.school_status || "",
        },
      }}
    >
      {({ register, formState }) => (
        <>
          <div className="d-flex gap-2">
            <div style={{ flex: 1 }}>
              <Input
                type="date"
                label="日付"
                name="date"
                placeholder="日付"
                error={formState.errors.date}
                registration={register("date")}
                disabled={true}
              />
            </div>
            <div style={{ flex: 1 }}>
              <Select
                label="授業種別"
                options={[
                  { value: "", label: "選択してください" },
                  { value: "test", label: "進級テスト" },
                  { value: "regular", label: "通常" },
                  { value: "none", label: "なし" },
                ]}
                error={formState.errors["lesson_type"]}
                registration={register("lesson_type")}
              />
            </div>
            <div style={{ flex: 1 }}>
              <Select
                label="営業種別"
                options={[
                  { value: "", label: "選択してください" },
                  { value: "business", label: "営業日" },
                  { value: "holiday", label: "休校日" },
                  { value: "closed", label: "休業日" },
                ]}
                error={formState.errors["school_status"]}
                registration={register("school_status")}
              />
            </div>
          </div>
          <div className="d-flex justify-content-end">
            <button
              type="submit"
              className="btn btn-primary"
              disabled={
                postCourseCalendarMutation.isPending ||
                putCourseCalendarMutation.isPending
              }
            >
              保存
            </button>
          </div>
        </>
      )}
    </Form>
  );
};

const CourseCalendar = () => {
  const [open, setOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());

  const calendarByDateRangeQuery = useCalendarByDateRangeQuery({
    start_date: moment(selectedDate).startOf("month").format("YYYY-MM-DD"),
    end_date: moment(selectedDate).endOf("month").format("YYYY-MM-DD"),
    queryConfig: {
      enabled: !!selectedDate,
    },
  });

  const postCalendarGenerateMonthMutation =
    usePostCalendarGenerateMonthMutation({
      mutationConfig: {
        onSuccess: data => {
          toast.success(data?.message);
        },
      },
    });

  const calendarInfo = date => {
    const calendar = calendarByDateRangeQuery.data?.data?.find(
      item => item.date === moment(date).format("YYYY-MM-DD")
    );
    return calendar;
  };

  return (
    <>
      <div>
        <button onClick={() => setOpen(true)} className="dash-btn">
          コース日カレンダー
        </button>
      </div>
      <Modal
        show={open}
        onHide={() => setOpen(false)}
        centered
        aria-labelledby="contained-modal-title-vcenter"
      >
        <Modal.Header closeButton>
          <Modal.Title id="contained-modal-title-vcenter">
            コース日カレンダー
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-3 d-flex justify-content-end">
            <button
              className="btn btn-primary"
              onClick={() =>
                postCalendarGenerateMonthMutation.mutate({
                  month: moment(selectedDate).format("YYYY-MM"),
                })
              }
            >
              {moment(selectedDate).format("YYYY-MM")} のデータを生成
            </button>
          </div>
          <div className="monthly-update-container">
            <div className="calendar-section">
              <Calendar
                onClickMonth={startOfMonth => {
                  setSelectedDate(startOfMonth);
                }}
                onClickDay={day => {
                  setSelectedDate(day);
                }}
                onActiveStartDateChange={({ activeStartDate }) => {
                  setSelectedDate(activeStartDate);
                }}
                value={selectedDate}
                tileClassName={({ date }) => {
                  const calendar = calendarInfo(date);

                  if (moment(date).isSame(selectedDate, "day")) {
                    return "custom-calendar custom-calendar-selected";
                  }

                  if (calendar?.school_status === "closed") {
                    return "custom-calendar custom-calendar-closed";
                  }

                  if (calendar?.school_status === "holiday") {
                    return "custom-calendar custom-calendar-holiday";
                  }

                  if (calendar?.lesson_type === "test") {
                    return "custom-calendar custom-calendar-test";
                  }

                  return "custom-calendar";
                }}
              />
              <CalendarLegend />
            </div>
            <div className="content-section">
              <div className="mt-3">
                {selectedDate && (
                  <DateForm
                    key={calendarInfo(selectedDate)?.id}
                    calendar={
                      calendarInfo(selectedDate) || {
                        date: moment(selectedDate).format("YYYY-MM-DD"),
                      }
                    }
                  />
                )}
              </div>
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default CourseCalendar;
